import { useRef, useState } from 'react'

import { api } from '../api/client'
import type { FirmwareFileDTO, FirmwareProbeDTO } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable } from '../components/DataTable'
import { formatBytes } from '../components/charts'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Rete → Firmware (TFTP): il catalogo dei firmware che le sonde servono in TFTP
 * agli apparati (switch, router). Si carica un file (a blocchi, con ripresa) e
 * si scelgono le sonde che lo serviranno; la sonda lo scarica dal Control Plane,
 * ne verifica lo SHA-256 e lo serve in **sola lettura** su UDP/69.
 *
 * Lo spazio del catalogo ha un tetto (4 GB per tenant); la barra lo mostra.
 *
 * Every file expires (22/10/2026): past its date it leaves the catalogue by
 * itself and the probes drop it within a minute. The default for new files is
 * the tenant's; each row can be extended or set to never expire.
 */

const GIORNO = 24 * 60 * 60 * 1000
const SCADENZE_PREDEFINITE = [7, 30, 90, 180, 365]

function fraGiorni(giorni: number): string {
  return new Date(Date.now() + giorni * GIORNO).toISOString()
}


const CHUNK = 16 * 1024 * 1024 // sotto il limite del WAF (64 MiB)

export default function Firmware() {
  const catalogo = useAsync(() => api.firmwareCatalog(), [])
  const sonde = useAsync(() => api.firmwareProbes(), [])
  const [errore, setErrore] = useState<string | null>(null)
  const [avanzamento, setAvanzamento] = useState<number | null>(null)
  const [selezione, setSelezione] = useState<FirmwareFileDTO | null>(null)
  const inputFile = useRef<HTMLInputElement>(null)

  const dati = catalogo.data

  async function carica(file: File) {
    setErrore(null)
    setAvanzamento(0)
    try {
      const inizio = await api.firmwareBeginUpload({ name: file.name, size: file.size })
      const id = inizio.upload_id
      let offset = inizio.received
      while (offset < file.size) {
        const pezzo = file.slice(offset, Math.min(offset + CHUNK, file.size))
        const stato = await api.firmwareAppendChunk(id, offset, pezzo)
        offset = stato.received // se c'è un disallineamento, riprende da qui
        setAvanzamento(file.size ? offset / file.size : 1)
      }
      await api.firmwareFinalize(id)
      await catalogo.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    } finally {
      setAvanzamento(null)
      if (inputFile.current) inputFile.current.value = ''
    }
  }

  async function elimina(file: FirmwareFileDTO) {
    if (!window.confirm(t('Togliere «{nome}» dal catalogo? Le sonde lo rimuoveranno.', { nome: file.name })))
      return
    setErrore(null)
    try {
      await api.firmwareDelete(file.id)
      if (selezione?.id === file.id) setSelezione(null)
      await catalogo.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  async function scadenza(file: FirmwareFileDTO, scelta: string) {
    if (!scelta) return
    setErrore(null)
    try {
      await api.firmwareSetExpiry(file.id, scelta === 'mai' ? null : fraGiorni(Number(scelta)))
      await catalogo.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  async function predefinita(giorni: number) {
    setErrore(null)
    try {
      await api.firmwareSetRetention(giorni)
      await catalogo.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  async function assegna(file: FirmwareFileDTO, applianceIds: string[]) {
    setErrore(null)
    try {
      await api.firmwareSetProbes(file.id, applianceIds)
      await catalogo.reload()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  if (catalogo.loading && !dati) return <Spinner />

  const usato = dati?.used_bytes ?? 0
  const tetto = dati?.quota_bytes ?? 0
  const quota = tetto ? Math.min(100, (usato / tetto) * 100) : 0

  return (
    <div className="page">
      <div className="pageheader">
        <h1>{t('Firmware (TFTP)')}</h1>
        <p className="muted">
          {t('carica il file e scegli le sonde che lo renderanno disponibile agli asset tramite TFTP. Prima della distribuzione, ogni sonda scarica il file dal Control Plane e ne verifica l’integrità. TFTP è utilizzato esclusivamente dalla sonda verso gli asset.')}
        </p>
      </div>

      {errore && <ErrorBanner error={errore} />}

      <Card>
        <div className="field">
          <label>{t('Spazio del catalogo')}</label>
          <div style={{ background: 'var(--superficie-2, #eee)', borderRadius: 4, height: 10, overflow: 'hidden' }}>
            <div style={{ width: `${quota}%`, height: '100%', background: quota > 90 ? 'var(--rosso, #c0392b)' : 'var(--accento, #2d7)' }} />
          </div>
          <p className="field__hint">
            {t('{usato} di {tetto} usati', { usato: formatBytes(usato), tetto: formatBytes(tetto) })}
          </p>
        </div>

        <div className="rowactions" style={{ textAlign: 'left' }}>
          <input
            ref={inputFile}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) carica(f)
            }}
          />
          <button
            className="pulsante-link"
            disabled={avanzamento !== null}
            onClick={() => inputFile.current?.click()}
          >
            {avanzamento !== null
              ? t('Caricamento… {pct}%', { pct: Math.round((avanzamento ?? 0) * 100) })
              : t('Carica un firmware')}
          </button>
          <span className="field__hint" style={{ marginLeft: 8 }}>
            {t('Il caricamento avviene a blocchi per gestire anche file di grandi dimensioni; in caso di interruzione della rete, riprende automaticamente.')}
          </span>
        </div>

        <div className="field" style={{ maxWidth: 360, marginTop: 12 }}>
          <label htmlFor="fw-scadenza">{t('Scadenza dei nuovi firmware')}</label>
          <select
            id="fw-scadenza"
            value={String(dati?.retention_days ?? 30)}
            onChange={(e) => predefinita(Number(e.target.value))}
          >
            {SCADENZE_PREDEFINITE.map((g) => (
              <option key={g} value={String(g)}>
                {t('dopo {n} giorni', { n: g })}
              </option>
            ))}
            <option value="0">{t('mai')}</option>
          </select>
          <p className="field__hint">
            {t('Un firmware scaduto esce dal catalogo da solo e le sonde lo tolgono entro un minuto. Vale per i file caricati d’ora in poi; per quelli già nel catalogo si cambia riga per riga.')}
          </p>
        </div>
      </Card>

      <Card>
        {!dati || dati.files.length === 0 ? (
          <EmptyState title={t('Nessun firmware nel catalogo. Carica il primo file qui sopra.')} />
        ) : (
          <DataTable
            rows={dati.files}
            rowKey={(f) => f.id}
            initialSort={{ key: 'name' }}
            columns={[
              {
                key: 'name',
                header: t('Nome'),
                value: (f) => f.name,
                render: (f) => (
                  <>
                    {f.name}
                    {f.missing && (
                      <span className="badge badge--warn" style={{ marginLeft: 6 }} title={t('Il file non è più sul Control Plane: le sonde non possono scaricarlo. Eliminalo e caricalo di nuovo.')}>
                        {t('file mancante')}
                      </span>
                    )}
                  </>
                ),
              },
              { key: 'size', header: t('Dimensione'), value: (f) => f.size, render: (f) => formatBytes(f.size) },
              {
                key: 'probe_ids',
                header: t('Sonde'),
                value: (f) => f.probe_ids.length,
                render: (f) => (f.probe_ids.length === 0 ? t('nessuna') : String(f.probe_ids.length)),
              },
              { key: 'sha256', header: 'SHA-256', value: (f) => f.sha256, render: (f) => f.sha256.slice(0, 16) + '…' },
              {
                key: 'expires_at',
                header: t('Scade'),
                value: (f) => f.expires_at ?? '',
                render: (f) => {
                  if (!f.expires_at) return <span className="muted">{t('mai')}</span>
                  const quando = new Date(f.expires_at)
                  const giorni = Math.ceil((quando.getTime() - Date.now()) / GIORNO)
                  const data = quando.toLocaleDateString(localeCorrente(), { day: 'numeric', month: 'short', year: 'numeric' })
                  return giorni <= 7 ? (
                    <span className="badge badge--warn" title={t('fra {n} giorni', { n: Math.max(giorni, 0) })}>{data}</span>
                  ) : (
                    <span title={t('fra {n} giorni', { n: giorni })}>{data}</span>
                  )
                },
              },
              {
                key: 'azioni',
                header: t('Azioni'),
                value: () => '',
                render: (f) => (
                  <span className="rowactions">
                    <button className="link" onClick={() => setSelezione(f)}>{t('Sonde')}</button>{' '}
                    <select
                      aria-label={t('Scadenza di «{nome}»', { nome: f.name })}
                      value=""
                      onChange={(e) => scadenza(f, e.target.value)}
                    >
                      <option value="">{t('Scadenza…')}</option>
                      {[7, 30, 90].map((g) => (
                        <option key={g} value={String(g)}>{t('fra {n} giorni', { n: g })}</option>
                      ))}
                      <option value="mai">{t('non scade')}</option>
                    </select>{' '}
                    <button className="link" onClick={() => elimina(f)}>{t('Elimina')}</button>
                  </span>
                ),
              },
            ]}
          />
        )}
      </Card>

      {selezione && (
        <Card>
          <div className="field">
            <label>{t('Quali sonde servono «{nome}»', { nome: selezione.name })}</label>
            <p className="field__hint">
              {t('Solo le sonde con la capability «Firmware (TFTP)» attiva. Attivala nella pagina Sonde, scheda capability.')}
            </p>
            {sonde.loading && <Spinner />}
            {sonde.data && sonde.data.filter((s) => s.tftp).length === 0 && (
              <EmptyState title={t('Nessuna sonda ha la capability «Firmware (TFTP)» attiva.')} />
            )}
            {sonde.data?.filter((s) => s.tftp).map((s: FirmwareProbeDTO) => {
              const scelta = selezione.probe_ids.includes(s.id)
              return (
                <label key={s.id} style={{ display: 'block', margin: '4px 0' }}>
                  <input
                    type="checkbox"
                    checked={scelta}
                    onChange={(e) => {
                      const ids = e.target.checked
                        ? [...selezione.probe_ids, s.id]
                        : selezione.probe_ids.filter((x) => x !== s.id)
                      const aggiornato = { ...selezione, probe_ids: ids }
                      setSelezione(aggiornato)
                      assegna(aggiornato, ids)
                    }}
                  />{' '}
                  {s.name}
                  {!s.enabled && <span className="muted"> {t('(sonda disattivata)')}</span>}
                </label>
              )
            })}
            <div className="rowactions">
              <button className="link" onClick={() => setSelezione(null)}>{t('Chiudi')}</button>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
